import { useState } from "react";
import { Formik, Form } from "formik";
import { IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

import { BrandedSplitLayout } from "../../../branding/components/BrandedSplitLayout";
import { BrandedFormHeader } from "../../../branding/components/BrandedFormHeader";
import { BrandedSecondaryAction } from "../../../branding/components/BrandedSecondaryAction";
import { BrandedResultMessage } from "../../../branding/components/BrandedResultMessage";
import { BrandedTextField } from "../../../branding/components/BrandedTextField";
import { BrandedButton } from "../../../branding/components/BrandedButton";
import { PasswordStrengthBar } from "../../../components/PasswordStrengthBar";
import { routesConfig } from "../../../config/routes";
import { useContextualNavigate } from "../../../hooks/ContextualNavigations";
import { showPass } from "../../../utils";

import { validationSchemaUpdatePassword } from "../UpdatePasswordDefault/form/validationSchema";
import { initialValuesFormikUpdatePassword } from "../UpdatePasswordDefault/form/initialValues";
import { UpdatePasswordFormFields } from "../UpdatePasswordDefault/form/interfaceValues";
import { useUpdatePasswordSubmit } from "../UpdatePasswordDefault/hook/useUpdatePasswordSubmit";

// UpdatePassword Branded (primeiro acesso), padrão D12. Reusa useUpdatePasswordSubmit — mesma
// lógica de auth da Default. Campos = BrandedTextField; botão = BrandedButton; medidor compacto.
const UpdatePasswordBranded = (): JSX.Element => {
  const navigate = useContextualNavigate();
  const { handleSubmit, successUpdatePassword, email } = useUpdatePasswordSubmit();
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const goToLogin = () => navigate(routesConfig.LOGIN);

  if (successUpdatePassword) {
    return (
      <BrandedSplitLayout>
        <BrandedResultMessage
          title="Senha atualizada"
          message="Sua nova senha foi salva com sucesso."
          primaryLabel="Fazer login"
          onPrimary={goToLogin}
        />
      </BrandedSplitLayout>
    );
  }

  return (
    <Formik
      validateOnMount
      initialValues={{ ...initialValuesFormikUpdatePassword, email }}
      validationSchema={validationSchemaUpdatePassword}
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, isValid, isSubmitting, handleChange, handleBlur }) => (
        <Form>
          <BrandedSplitLayout>
            <BrandedFormHeader
              title="Primeiro acesso"
              subtitle="Crie uma nova senha para garantir a sua segurança."
            />

            <BrandedTextField
              id="email"
              type="email"
              name={UpdatePasswordFormFields.email}
              label="E-mail"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled
              error={Boolean(touched.email) && Boolean(errors.email)}
              helperText={touched.email && (errors.email as string)}
            />

            <BrandedTextField
              type={showPass(showNew)}
              name={UpdatePasswordFormFields.newPass}
              label="Nova senha"
              value={values.newPass}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.newPass && Boolean(errors.newPass)}
              helperText={touched.newPass && errors.newPass}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" onClick={() => setShowNew((s) => !s)}>
                      {showNew ? <VisibilityOff color="primary" /> : <Visibility color="primary" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <BrandedTextField
              type={showPass(showConfirm)}
              name={UpdatePasswordFormFields.newPassConfirmation}
              label="Confirme a nova senha"
              value={values.newPassConfirmation}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.newPassConfirmation && Boolean(errors.newPassConfirmation)}
              helperText={touched.newPassConfirmation && errors.newPassConfirmation}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" onClick={() => setShowConfirm((s) => !s)}>
                      {showConfirm ? <VisibilityOff color="primary" /> : <Visibility color="primary" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <PasswordStrengthBar
              newPass={values.newPass}
              newPassConfirmation={values.newPassConfirmation}
              compact
            />

            <BrandedButton type="submit" loading={isSubmitting} disabled={!isValid}>
              Salvar nova senha
            </BrandedButton>

            <BrandedSecondaryAction actions={[{ label: "Voltar para o login", to: routesConfig.LOGIN }]} />
          </BrandedSplitLayout>
        </Form>
      )}
    </Formik>
  );
};

export default UpdatePasswordBranded;
